import { FC, useEffect, useState } from 'react';
import cn from 'classnames';
import styles from './LandscapeBasicInformationEditPage.module.css';
import { useNavigate, useParams } from 'react-router-dom';
import { LandscapeBasicInformationEditTemplate } from '../../templates';
import { useGetLandscapeById } from '../../../store/landscapeAPI/landscape/queries/landscape.queries';
import { mapEntityByLanguage } from '../../../utils/Helpers/language-helper';
import { useMutation } from '@tanstack/react-query';
import { UpdateInitiativeDto } from '../../../store/landscapeAPI/landscape/dto/request/update-initiative-dto';
import landscapeServiceV3 from '../../../store/landscapeAPI/landscape/services/landspace.service-v3';
import { getQueryClient } from '../../../App';
import Spinner from '../../atoms/Spinner/Spinner';

interface LandscapeBasicInformationEditPageProps {}

const LandscapeBasicInformationAddPage: FC<
  LandscapeBasicInformationEditPageProps
> = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const clientQuery = getQueryClient();
  const [landscape, setLandscape] = useState<Map<string, any>>();

  const { data: _landscape } = useGetLandscapeById(id || '');

  useEffect(() => {
    if (!_landscape) return;
    const landscapeMappedByLanguage = mapEntityByLanguage(_landscape);
    setLandscape(landscapeMappedByLanguage);
  }, [_landscape]);

  const keysToInvalidate = [
    { queryKey: ['landscape', id] },
    { queryKey: ['landscape', 'landscapes', 'name', 'asc'] },
  ];

  const deleteLandscape = async () => {
    if (id) {
      return await landscapeServiceV3.deleteLandscape(id);
    }
  };

  const { mutate: handleDeleteLandscape, isPending: isPendingDeleteLandscape } =
    useMutation({
      mutationFn: deleteLandscape,
      onSuccess: () => {
        navigate('/landscapes');
        clientQuery.invalidateQueries({
          queryKey: ['landscape', 'landscapes', 'name', 'asc'],
        });
      },
      onError: (error) => console.log(error),
    });

  const editLandscape = async ({
    landscape,
    languageCode,
    translateToAllLanguages,
  }: {
    landscape: UpdateInitiativeDto;
    languageCode: string;
    translateToAllLanguages: boolean;
  }) => {
    const body: UpdateInitiativeDto = {
      compactId: id as string,
      name: landscape.name,
      landscapeType: landscape.landscapeType,
      jurisdictionName: landscape.jurisdictionName,
      countryCode: landscape.countryCode,
      latitude: landscape.latitude,
      longitude: landscape.longitude,
      isPublished: landscape.isPublished,
      isLandscapeInitiative: landscape.isLandscapeInitiative,
    };

    await landscapeServiceV3.editLandscape(
      body,
      languageCode,
      translateToAllLanguages
    );
  };
  const { mutate: handleEditLandscape, isPending: isPendingSave } = useMutation(
    {
      mutationFn: editLandscape,
      onSuccess: () => {
        keysToInvalidate.forEach((key) => clientQuery.invalidateQueries(key));
      },
    }
  );

  if (isPendingSave || isPendingDeleteLandscape) {
    return <Spinner />;
  }

  return (
    <>
      {landscape && (
        <div
          className={cn(styles.landscapeBasicInformationEditTemplateWrapper)}
        >
          <LandscapeBasicInformationEditTemplate
            landscape={landscape}
            onDeleteClick={handleDeleteLandscape}
            callbackFn={(landscape, languageCode, translateToAllLanguages) =>
              handleEditLandscape({
                // @ts-ignore
                landscape,
                languageCode,
                translateToAllLanguages,
              })
            }
          />
        </div>
      )}
    </>
  );
};

export default LandscapeBasicInformationAddPage;
